package com.schoolconnect.app.datasource.network.response

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class TeacherStatisticsResponse(
    @SerialName("total_students")
    val totalStudents: Int? = null,
    @SerialName("assigned_classes_count")
    val assignedClassesCount: Int? = null,
    @SerialName("assigned_classes")
    val assignedClasses: List<TeacherClassStatistics> = emptyList(),
    @SerialName("good_performance_count")
    val goodPerformanceCount: Int? = null,
    @SerialName("needs_attention_count")
    val needsAttentionCount: Int? = null,
    @SerialName("good_performance_students")
    val goodPerformanceStudents: List<TeacherPerformanceStudent> = emptyList(),
    @SerialName("needs_attention_students")
    val needsAttentionStudents: List<TeacherPerformanceStudent> = emptyList()
)

@Serializable
data class TeacherClassStatistics(
    @SerialName("class_section_id")
    val classSectionId: String? = null,
    @SerialName("class_name")
    val className: String? = null,
    @SerialName("section_name")
    val sectionName: String? = null,
    @SerialName("total_students")
    val totalStudents: Int? = null,
    @SerialName("boys_count")
    val boysCount: Int? = null,
    @SerialName("girls_count")
    val girlsCount: Int? = null
)

@Serializable
data class TeacherPerformanceStudent(
    @SerialName("student_id")
    val studentId: String? = null,
    @SerialName("student_name")
    val studentName: String? = null,
    @SerialName("admission_number")
    val admissionNumber: String? = null,
    @SerialName("class_name")
    val className: String? = null,
    @SerialName("section_name")
    val sectionName: String? = null,
    @SerialName("average_score")
    val averageScore: Double? = null,
    @SerialName("average_percentage")
    val averagePercentage: Double? = null,
    @SerialName("total_exams")
    val totalExams: Int? = null
)

@Serializable
data class TeacherStudentListResponse(
    val count: Int? = null,
    @SerialName("academic_year_id")
    val academicYearId: String? = null,
    @SerialName("academic_year_label")
    val academicYearLabel: String? = null,
    val message: String? = null,
    val students: List<TeacherStudentListItem> = emptyList()
)

@Serializable
data class TeacherStudentListItem(
    @SerialName("student_id")
    val studentId: String? = null,
    // Some deployed dashboard responses expose the UUID as `id` instead of `student_id`.
    @SerialName("id")
    val id: String? = null,
    @SerialName("roll_number")
    val rollNumber: String? = null,
    val name: String? = null,
    @SerialName("class_name")
    val className: String? = null,
    @SerialName("section_name")
    val sectionName: String? = null,
    val gender: String? = null,
    @SerialName("attendance_percentage")
    val attendancePercentage: Double? = null,
    @SerialName("average_exam_grade")
    val averageExamGrade: Double? = null,
    @SerialName("subject_codes")
    val subjectCodes: List<String>? = null
)

@Serializable
data class TeacherStudentAttendanceStatsResponse(
    @SerialName("student_id")
    val studentId: String? = null,
    @SerialName("student_name")
    val studentName: String? = null,
    @SerialName("class_name")
    val className: String? = null,
    @SerialName("section_name")
    val sectionName: String? = null,
    @SerialName("total_days")
    val totalDays: Int? = null,
    @SerialName("present_count")
    val presentCount: Int? = null,
    @SerialName("absent_count")
    val absentCount: Int? = null,
    @SerialName("late_count")
    val lateCount: Int? = null,
    @SerialName("attendance_percentage")
    val attendancePercentage: Double? = null,
    val message: String? = null
)

@Serializable
data class TeacherStudentAttendanceRecordsResponse(
    @SerialName("student_id")
    val studentId: String? = null,
    @SerialName("student_name")
    val studentName: String? = null,
    @SerialName("class_name")
    val className: String? = null,
    @SerialName("section_name")
    val sectionName: String? = null,
    val message: String? = null,
    val count: Int? = null,
    val records: List<TeacherStudentAttendanceRecord> = emptyList()
)

@Serializable
data class TeacherStudentAttendanceRecord(
    val id: String? = null,
    @SerialName("attendance_date")
    val attendanceDate: String? = null,
    val day: String? = null,
    val status: String? = null,
    val remarks: String? = null,
    @SerialName("recorded_by_staff_id")
    val recordedByStaffId: String? = null,
    @SerialName("created_at")
    val createdAt: String? = null
)
